import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { AppointmentModal } from '../calendar/AppointmentModal';
import { CASE_TYPES } from '../../constants/caseTypes';

const CREATE_CASE = gql`
  mutation CreateCase($input: CreateCaseInput!) {
    createCase(input: $input) {
      id
      caseNumber
      status
    }
  }
`;

const LIST_CLIENTS = gql`
  query ListClients {
    listClients {
      id
      firstName
      lastName
      email
    }
  }
`;

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface ListClientsData {
  listClients: Client[];
}

interface CreateCaseData {
  createCase: {
    id: string;
    caseNumber: string;
    status: string;
  };
}

export function QuickActionsWidget() {
  const navigate = useNavigate();
  const [showCreateCase, setShowCreateCase] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [clientId, setClientId] = useState('');
  const [caseType, setCaseType] = useState('');
  const [customCaseType, setCustomCaseType] = useState('');
  const [description, setDescription] = useState('');
  const [incidentDate, setIncidentDate] = useState('');
  const [createCase, { loading }] = useMutation<CreateCaseData>(CREATE_CASE);
  
  // Get list of clients (with error handling for when infrastructure isn't deployed yet)
  const { data: clientsData, loading: loadingClients, error: clientsError } = useQuery<ListClientsData>(LIST_CLIENTS);
  
  // Extract unique clients, or show empty if query fails
  const clients = clientsError ? [] : (clientsData?.listClients || []);

  const handleCreateCase = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const finalCaseType = caseType === 'Other' ? customCaseType : caseType;
    
    if (!clientId || !finalCaseType.trim() || !description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const result = await createCase({
        variables: {
          input: {
            clientId,
            caseType: finalCaseType,
            description,
            incidentDate: incidentDate || undefined,
          },
        },
        refetchQueries: ['ListCases'],
      });
      
      setClientId('');
      setCaseType('');
      setCustomCaseType('');
      setDescription('');
      setIncidentDate('');
      setShowCreateCase(false);
      
      // Navigate to the new case
      if (result.data?.createCase?.id) {
        navigate(`/cases/${result.data.createCase.id}`);
      } else {
        alert('Case created successfully!');
      }
    } catch (error: any) {
      console.error('Error creating case:', error);
      alert('Error creating case: ' + error.message);
    }
  };

  const quickActions = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      label: 'Create Case',
      color: 'bg-aalaw-red hover:bg-aalaw-red-dark',
      onClick: () => setShowCreateCase(!showCreateCase),
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      label: 'New Appointment',
      color: 'bg-blue-600 hover:bg-blue-700',
      onClick: () => setShowAppointmentModal(true),
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      label: 'Call Client',
      color: 'bg-blue-600 hover:bg-blue-700',
      onClick: () => alert('Call feature coming soon!'),
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      label: 'Send Email',
      color: 'bg-green-600 hover:bg-green-700',
      onClick: () => alert('Email feature coming soon!'),
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      label: 'Upload Document',
      color: 'bg-gray-600 hover:bg-gray-700',
      onClick: () => alert('Document upload coming soon!'),
    },
  ];

  return (
    <>
      <Card className="h-full">
        <CardHeader className="border-b border-gray-100">
          <CardTitle className="text-base font-semibold text-aalaw-charcoal">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {showCreateCase ? (
            <form onSubmit={handleCreateCase} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client <span className="text-red-500">*</span>
                </label>
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  required
                  disabled={loadingClients}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-aalaw-red focus:border-aalaw-red"
                >
                  <option value="">
                    {loadingClients ? 'Loading clients...' : 'Select a client'}
                  </option>
                  {clients.map((client: any) => (
                    <option key={client.id} value={client.id}>
                      {client.firstName} {client.lastName} ({client.email})
                    </option>
                  ))}
                </select>
                {!loadingClients && clients.length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {clientsError 
                      ? 'Deploy infrastructure to enable client selection' 
                      : 'No clients found. Create a client first.'}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Case Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={caseType}
                  onChange={(e) => {
                    setCaseType(e.target.value);
                    if (e.target.value !== 'Other') {
                      setCustomCaseType('');
                    }
                  }}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-aalaw-red focus:border-aalaw-red"
                >
                  <option value="">Select a case type</option>
                  {CASE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              {caseType === 'Other' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Case Type <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={customCaseType}
                    onChange={(e) => setCustomCaseType(e.target.value)}
                    placeholder="Enter custom case type"
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Incident Date
                </label>
                <Input
                  type="date"
                  value={incidentDate}
                  onChange={(e) => setIncidentDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief case description..."
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-aalaw-red focus:border-aalaw-red"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={loading || loadingClients || clients.length === 0} className="flex-1">
                  {loading ? 'Creating...' : 'Create Case'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateCase(false);
                    setClientId('');
                    setCaseType('');
                    setCustomCaseType('');
                    setDescription('');
                    setIncidentDate('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className={`${action.color} text-white p-3 rounded-lg transition-all hover:shadow-md flex flex-col items-center justify-center gap-2`}
                >
                  <div className="flex items-center justify-center">{action.icon}</div>
                  <div className="text-xs font-medium">{action.label}</div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Appointment Modal */}
      {showAppointmentModal && (
        <AppointmentModal
          onClose={() => setShowAppointmentModal(false)}
        />
      )}
    </>
  );
}
